import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { bitable } from '@lark-base-open/js-sdk';
import { DETAIL_FIELDS, FieldMeta, normalizeFields, Resource } from './resource';
import { highlightParts, searchResources, SearchResult } from './search';
import './styles.css';

type TableLike = {
  id: string;
  getFieldMetaList: () => Promise<FieldMeta[]>;
  getRecordsByPage: (params: { pageSize: number; pageToken?: number }) => Promise<{ records: Array<{ id?: string; recordId?: string; record_id?: string; fields?: Record<string, unknown> }>; hasMore?: boolean; pageToken?: number }>;
  getRecordShareLink: (recordId: string) => Promise<string>;
  onRecordAdd: (callback: () => void) => () => void;
  onRecordModify: (callback: () => void) => () => void;
  onRecordDelete: (callback: () => void) => () => void;
};

async function loadResources(table: TableLike): Promise<{ resources: Resource[]; metas: FieldMeta[] }> {
  const metas = await table.getFieldMetaList();
  const resources: Resource[] = [];
  let pageToken: number | undefined;
  do {
    const page = await table.getRecordsByPage({ pageSize: 200, ...(pageToken ? { pageToken } : {}) });
    resources.push(...page.records.map((record) => normalizeFields(record, metas)));
    pageToken = page.hasMore ? page.pageToken : undefined;
  } while (pageToken);
  return { resources, metas };
}

function safeExternalUrl(value: string): string | null {
  try {
    const url = new URL(value.trim());
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.toString() : null;
  } catch {
    return null;
  }
}

function Marked({ text, query }: { text: string; query: string }) {
  return <>{highlightParts(text, query).map((part, index) => part.match ? <mark key={index}>{part.text}</mark> : <React.Fragment key={index}>{part.text}</React.Fragment>)}</>;
}

function App() {
  const [table, setTable] = useState<TableLike | null>(null);
  const [resources, setResources] = useState<Resource[]>([]);
  const [metas, setMetas] = useState<FieldMeta[]>([]);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<SearchResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const activeTable = table ?? await bitable.base.getActiveTable() as unknown as TableLike;
      const loaded = await loadResources(activeTable);
      setTable(activeTable); setMetas(loaded.metas); setResources(loaded.resources); setUpdatedAt(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : '无法读取当前资源表');
    } finally { setLoading(false); }
  }, [table]);

  useEffect(() => { void refresh(); }, [refresh]);
  useEffect(() => {
    if (!table) return;
    let timer: number | undefined;
    const schedule = () => { window.clearTimeout(timer); timer = window.setTimeout(() => void refresh(), 300); };
    const unsubscribers = [table.onRecordAdd(schedule), table.onRecordModify(schedule), table.onRecordDelete(schedule)];
    return () => { window.clearTimeout(timer); unsubscribers.forEach((unsubscribe) => unsubscribe?.()); };
  }, [table, refresh]);

  const results = useMemo(() => searchResources(resources, query), [resources, query]);

  async function openRecord() {
    if (!selected || !table) return;
    await bitable.ui.showRecordDetailDialog({ tableId: table.id, recordId: selected.id, fieldIdList: DETAIL_FIELDS.map((field) => metas.find((meta) => meta.name === field)?.id).filter((id): id is string => Boolean(id)) });
  }

  async function openShareLink() {
    if (!selected || !table) return;
    const url = safeExternalUrl(selected.fields['原始链接'] ?? '');
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
    else window.open(await table.getRecordShareLink(selected.id), '_blank', 'noopener,noreferrer');
  }

  if (selected) return <main className="shell detail-shell">
    <button className="back-button" onClick={() => setSelected(null)}>← 返回搜索结果</button>
    <div className="detail-header"><div><span className="eyebrow">资源详情</span><h1>{selected.fields['资源名称'] || '未命名资源'}</h1><p>{selected.fields['一句话价值']}</p></div><span className="score-badge">相关度 {selected.score}</span></div>
    <div className="chips">{[selected.fields['主赛道'], selected.fields['资源类型'], selected.fields['标签']].filter(Boolean).map((item) => <span key={item} className="chip">{item}</span>)}</div>
    <section className="detail-grid">{DETAIL_FIELDS.filter((field) => field !== '资源正文' && field !== '资源名称' && field !== '一句话价值').map((field) => <div className="field-card" key={field}><span>{field}</span><strong>{selected.fields[field] || '—'}</strong></div>)}</section>
    <section className="body-card"><div className="section-label">资源正文</div><div className="resource-body">{selected.fields['资源正文'] || '暂无正文'}</div></section>
    <div className="actions"><button className="primary" onClick={() => void openRecord()}>在飞书记录中打开</button><button className="secondary" onClick={() => void openShareLink()}>{selected.fields['原始链接'] ? '打开原始链接' : '打开飞书记录链接'}</button></div>
  </main>;

  return <main className="shell"><header className="hero"><div className="eyebrow">AI者们 · 资源库</div><h1>你要找的资源，<em>一搜就到。</em></h1><p>搜索标题、价值、标签和正文。结果会告诉你命中了哪里，点击即可查看完整资源。</p><div className="search-wrap"><span className="search-icon">⌕</span><input autoFocus value={query} onChange={(event) => setQuery(event.target.value)} placeholder="试试：提示词、ComfyUI、电商选品…" /><kbd>⌘ K</kbd></div><div className="hero-meta"><span>{loading ? '正在读取资源…' : `${resources.length} 条资源已就绪`}</span><button onClick={() => void refresh()} disabled={loading}>刷新</button>{updatedAt && <span>更新于 {updatedAt.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })}</span>}</div></header>
    {error && <div className="notice error">{error}<button onClick={() => void refresh()}>重试</button></div>}
    {!query.trim() && !loading && <section className="empty-state"><div className="orb">✦</div><h2>从一个词开始</h2><p>输入你想解决的问题、工具名或技能名，搜索会同时覆盖资源正文和结构化标签。</p><div className="suggestions">{['提示词', '电商 AI', 'ComfyUI', 'AI 编程'].map((suggestion) => <button key={suggestion} onClick={() => setQuery(suggestion)}>{suggestion} →</button>)}</div></section>}
    {query.trim() && !loading && <section className="results"><div className="results-head"><span><b>{results.length}</b> 个结果</span><span>按相关度排序</span></div>{results.length === 0 ? <div className="no-results"><h2>没有找到“{query}”</h2><p>试试更短的关键词，或换一个同义词。</p></div> : results.map((result, index) => <article className="result-card" key={result.id} tabIndex={0} role="button" aria-label={`查看资源：${result.fields['资源名称'] || '未命名资源'}`} style={{ '--delay': `${index * 35}ms` } as React.CSSProperties} onClick={() => setSelected(result)} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setSelected(result); } }}><div className="result-top"><span className="result-index">{String(index + 1).padStart(2, '0')}</span><h2><Marked text={result.fields['资源名称'] || '未命名资源'} query={query} /></h2><span className="arrow">↗</span></div><p className="value"><Marked text={result.fields['一句话价值'] || '暂无一句话价值'} query={query} /></p><div className="chips">{[result.fields['主赛道'], result.fields['资源类型'], result.fields['标签']].filter(Boolean).map((item) => <span key={item} className="chip"><Marked text={item} query={query} /></span>)}</div><div className="hit-list">{result.hits.slice(0, 2).map((hit) => <div className="hit" key={hit.field}><span>{hit.field}</span><p><Marked text={hit.snippet} query={query} /></p></div>)}</div></article>)}</section>}
  </main>;
}

document.addEventListener('keydown', (event) => { if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') { event.preventDefault(); document.querySelector<HTMLInputElement>('input')?.focus(); } });
createRoot(document.getElementById('root')!).render(<App />);
